// dashboard/src/components/credits/AddCustomCreditsForm.tsx
import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { getCreditBalance, createRazorpayOrder, verifyRazorpayPayment } from '../../services/creditService';

interface AddCustomCreditsFormProps {
  onSuccess?: () => void;
}

// Define Razorpay interface
declare global {
  interface Window {
    Razorpay: any;
  }
}

const AddCustomCreditsForm: React.FC<AddCustomCreditsFormProps> = ({ onSuccess }) => {
  const [amount, setAmount] = useState<number>(100);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [currency, setCurrency] = useState<string>('INR');
  const queryClient = useQueryClient();

  // Get current credit balance to check if user has an active plan
  const { data: creditBalance, isLoading: isLoadingBalance } = useQuery({
    queryKey: ['creditBalance'],
    queryFn: getCreditBalance,
  });

  const hasActivePlan = creditBalance?.plan_credits?.current_plan &&
                       creditBalance.plan_credits.current_plan !== 'free';



  // Razorpay order creation
  const createOrderMutation = useMutation({
    mutationFn: createRazorpayOrder,
    onSuccess: (data) => {
      // Open Razorpay checkout
      openRazorpayCheckout(data);
    },
    onError: (error: Error) => {
      setError(error.message);
      setSuccessMessage(null);
      setIsProcessing(false);
    },
  });

  // Payment verification
  const verifyPaymentMutation = useMutation({
    mutationFn: verifyRazorpayPayment,
    onSuccess: (data) => {
      // Invalidate and refetch credit balance
      queryClient.invalidateQueries({ queryKey: ['creditBalance'] });
      queryClient.invalidateQueries({ queryKey: ['creditTransactions'] });

      // Show success message with balance info and next steps
      setSuccessMessage(`🎉 Payment successful! ${data.credits_added} custom credits have been added to your account. Ready to start crawling? Try the API playground!`);
      setAmount(100);
      setError(null);
      setIsProcessing(false);

      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);

      if (onSuccess) onSuccess();
    },
    onError: (error: Error) => {
      setError(error.message);
      setSuccessMessage(null);
      setIsProcessing(false);
    },
  });

  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const openRazorpayCheckout = (orderData: any) => {
    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      amount: orderData.amount, // Amount in smallest currency unit for Razorpay
      currency: orderData.currency,
      name: 'Crawly Carl',
      description: `${orderData.credits} Custom Credits`,
      order_id: orderData.order_id,
      handler: async (response: any) => {
        // Verify payment
        verifyPaymentMutation.mutate({
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_order_id: response.razorpay_order_id,
          razorpay_signature: response.razorpay_signature,
          credits: orderData.credits, // Number of credits being purchased
          currency: currency,
        });
      },
      modal: {
        ondismiss: () => {
          setIsProcessing(false);
        },
      },
      theme: {
        color: '#2563eb',
      },
    };

    const razorpay = new window.Razorpay(options);
    razorpay.open();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!hasActivePlan) {
      setError('You need an active subscription plan to purchase custom credits');
      return;
    }

    if (amount < 100) {
      setError('Minimum amount is 100 credits');
      return;
    }

    if (amount > 10000) {
      setError('Maximum amount is 10,000 credits');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setSuccessMessage(null);

    // Use Razorpay for payment
    createOrderMutation.mutate({ amount, currency });
  };

  if (isLoadingBalance) {
    return (
      <div className="bg-white shadow rounded-lg p-6 animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="h-4 bg-gray-200 rounded w-2/3 mb-6"></div>
        <div className="h-32 bg-gray-200 rounded"></div>
      </div>
    );
  }

  if (!hasActivePlan) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="text-center mb-6">
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Buy Custom Credits</h3>
          <p className="text-gray-600">
            Purchase additional credits that never expire
          </p>
        </div>

        <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-lg p-6 text-center">
          <div className="flex items-center justify-center mb-4">
            <span className="text-orange-500 text-3xl mr-3">🔒</span>
            <h4 className="text-xl font-semibold text-orange-900">Subscription Required</h4>
          </div>
          <p className="text-orange-800 mb-4">
            You need an active subscription plan to purchase custom credits.
          </p>
          <p className="text-sm text-orange-700 mb-6">
            Custom credits are designed to supplement your monthly plan allocation for occasional high usage periods.
          </p>
          <div className="bg-white rounded-lg p-4 border border-orange-200">
            <h5 className="font-medium text-orange-900 mb-2">Why do I need a plan?</h5>
            <ul className="text-sm text-orange-800 space-y-1 text-left">
              <li>• Custom credits are used after plan credits are exhausted</li>
              <li>• Plans provide your base monthly credit allocation</li>
              <li>• Custom credits are perfect for usage spikes beyond your plan</li>
              <li>• This ensures you have a sustainable base usage pattern</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Buy Custom Credits</h3>
        <p className="text-gray-600">
          Purchase additional credits that never expire - perfect for occasional high usage periods
        </p>
      </div>

      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-center mb-2">
          <span className="text-blue-500 text-xl mr-2">✅</span>
          <h4 className="font-semibold text-blue-900">Active Plan: {creditBalance?.plan_credits?.current_plan?.charAt(0).toUpperCase() + creditBalance?.plan_credits?.current_plan?.slice(1)}</h4>
        </div>
        <p className="text-sm text-blue-800">
          You can purchase custom credits to supplement your monthly plan allocation.
        </p>
      </div>

      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4 mb-6">
        <div className="flex items-center mb-2">
          <span className="text-green-500 text-xl mr-2">♾️</span>
          <h4 className="font-semibold text-green-900">Never Expire</h4>
        </div>
        <p className="text-sm text-green-800">
          Custom credits are used after your plan credits are exhausted and remain in your account indefinitely.
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
          <div className="flex items-center">
            <span className="text-red-500 mr-2">⚠️</span>
            {error}
          </div>
        </div>
      )}

      {successMessage && (
        <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <span className="text-green-500 mr-2">✅</span>
              {successMessage}
            </div>
            <button
              onClick={() => setSuccessMessage(null)}
              className="text-green-500 hover:text-green-700 ml-4 text-xl font-bold"
              aria-label="Close success message"
            >
              ×
            </button>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Credit Amount Selection */}
        <div>
          <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-3">
            Select Credit Amount
          </label>

          {/* Quick Select Buttons */}
          <div className="grid grid-cols-4 gap-2 mb-4">
            {[100, 500, 1000, 2500].map((quickAmount) => (
              <button
                key={quickAmount}
                type="button"
                onClick={() => setAmount(quickAmount)}
                className={`py-2 px-3 text-sm font-medium rounded-md border transition-colors ${
                  amount === quickAmount
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
                disabled={isProcessing}
              >
                {quickAmount.toLocaleString()}
              </button>
            ))}
          </div>

          {/* Custom Amount Input */}
          <div className="relative">
            <input
              type="number"
              id="amount"
              min="100"
              max="10000"
              step="100"
              value={amount}
              onChange={(e) => setAmount(parseInt(e.target.value) || 0)}
              className="w-full px-4 py-3 text-lg font-semibold border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center"
              placeholder="Enter custom amount"
              disabled={isProcessing}
            />
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
              credits
            </div>
          </div>

          <div className="mt-2 flex justify-between text-xs text-gray-500">
            <span>Minimum: 100 credits</span>
            <span>Maximum: 10,000 credits</span>
          </div>
        </div>

        <div>
          <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-1">
            Currency
          </label>
          <select
            id="currency"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={isProcessing}
          >
            <option value="INR">INR (₹)</option>
            <option value="USD">USD ($)</option>
          </select>
        </div>

        {/* Benefits Section */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
          <h4 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
            <span className="text-blue-500 mr-2">🎯</span>
            Why Choose Custom Credits?
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start">
              <span className="text-green-500 mr-2 mt-0.5">♾️</span>
              <div>
                <p className="font-medium text-blue-900">Never Expire</p>
                <p className="text-sm text-blue-700">Use them anytime, no rush</p>
              </div>
            </div>
            <div className="flex items-start">
              <span className="text-purple-500 mr-2 mt-0.5">⚡</span>
              <div>
                <p className="font-medium text-blue-900">Smart Usage</p>
                <p className="text-sm text-blue-700">Used after plan credits</p>
              </div>
            </div>
            <div className="flex items-start">
              <span className="text-orange-500 mr-2 mt-0.5">📈</span>
              <div>
                <p className="font-medium text-blue-900">Flexible</p>
                <p className="text-sm text-blue-700">Perfect for usage spikes</p>
              </div>
            </div>
            <div className="flex items-start">
              <span className="text-red-500 mr-2 mt-0.5">🚫</span>
              <div>
                <p className="font-medium text-blue-900">No Commitment</p>
                <p className="text-sm text-blue-700">Buy only when needed</p>
              </div>
            </div>
          </div>
        </div>

        {/* Purchase Button */}
        <button
          type="submit"
          disabled={isProcessing || amount < 100 || amount > 10000}
          className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-4 px-6 rounded-lg text-lg font-semibold hover:from-green-700 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
        >
          {isProcessing ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
              Processing Purchase...
            </div>
          ) : (
            <div className="flex items-center justify-center">
              <span className="mr-2">💳</span>
              Purchase {amount.toLocaleString()} Custom Credits
            </div>
          )}
        </button>
      </form>
    </div>
  );
};

export default AddCustomCreditsForm;
